/* Copyright (c) 2020-present CloudIO Inc */

import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../registry/Component.type';
import { Platform } from '../../../platform/Platform';
import { baseComponentManager } from '../../../registry/Components';
import { ItemType } from '../../../registry/ComponentRegistry';
import { defaultValue } from './CardContent';
import { checkForFixedChild } from '../../../utils/ManagerUtils';
import { getCustomChildItemTypes } from '../../../registry/Manager';

const name = 'Card Content';

const CardContentManager: ComponentManagerFn<'CardContent'> = (
  platform: Platform,
): ComponentManager<'CardContent'> => {
  return {
    ...baseComponentManager<'CardContent'>(platform, 'CardContent'),
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('CardContent'),
          'ActionButton',
          'AutoSizer',
          'BoxContainer',
          'BoxList',
          'Button',
          'ButtonDropdown',
          'ButtonGroup',
          'Calendar',
          'CalendarHeatmap',
          'CanvasGrid',
          'ChartJSTreemap',
          'Chartjs',
          'Code',
          'CombinationChart',
          'CommentsPanel',
          'D3Chart',
          'DataGrid',
          'DataList',
          'DataStoreReference',
          'DragSource',
          'DropTarget',
          'FlexPanel',
          'Form',
          'FormSearch',
          'H1',
          'H2',
          'H3',
          'H4',
          'H5',
          'H6',
          'HTML',
          'HorizontalSplitPanel',
          'OCRComponent',
          'RowPanel',
          'SearchForm',
          'SmartSearch',
          'Typography',
          'VerticalSplitPanel',
          'ViewAttachment',
          'WorkflowViewer',
          'AngularGauge',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    props: {
      noPadding: {
        label: 'Remove Padding',
        type: 'boolean',
      },
    },
    name,
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    icon: { iconName: 'io-card-content', iconType: 'cloudio' },
    description: `**Overview**<br/>
Card Content serves as the primary body container within a Card component. It is designed to house the core subject matter—whether that be data visualizations, forms, textual information, or complex layouts.
Unlike fixed containers, the Card Content area is structurally dynamic. It automatically adapts its height to fit the components placed inside it, ensuring that charts expand fully and forms remain accessible without requiring manual size adjustments.

**1. Spacing & Edge-to-Edge Layouts**<br/>
By default, the Card Content applies standard padding to ensure that text and inputs have appropriate breathing room from the card's edges. However, specific use cases require full-width presentation.
* **Remove Padding:** Disables the default internal spacing. Enable this property when displaying data grids, maps, or large charts that look best when extending to the exact edges of the container (an "edge-to-edge" design).

**2. Automatic Sizing Behavior**<br/>
The component is optimized for "Auto Layout" environments.
* **Dynamic Expansion:** The container grows vertically to accommodate its children.
* **Layout Safety:** It includes internal logic to prevent invalid fixed-layout children from breaking the card's structure, ensuring predictable rendering across different screen sizes.`,
  };
};

export default CardContentManager;
